import { AxiosPromise } from 'axios';
import {
  BaseRepository,
  IEnumerableResponse,
  ITableRequestParams
} from '@app/src/common/repository/BaseRepository';
import {
  IArea,
  IFormValues,
  ILocationItem,
  IOnsiteLocation,
  IOnsiteVisitor
} from '@app/src/components/event-management/model/Location';

interface IDownloadAreaReportParams {
  locationId: string;
  areaId: string;
  params: ITableRequestParams;
}

class LocationRepository extends BaseRepository {
  locationUrl = `${this.ereceptionstUrl}/locations`;

  public getLocations(
    params?: ITableRequestParams
  ): Promise<IEnumerableResponse<ILocationItem>> {
    return this.callGet<IEnumerableResponse<ILocationItem>>(
      this.locationUrl,
      params
    ).then(response => response.data);
  }

  public createLocation(
    params: IFormValues
  ): AxiosPromise<IEnumerableResponse<ILocationItem>> {
    return this.callPost<IEnumerableResponse<ILocationItem>>(
      this.locationUrl,
      params
    );
  }

  public updateLocation(
    params: ILocationItem
  ): AxiosPromise<IEnumerableResponse<ILocationItem>> {
    return this.callPut<IEnumerableResponse<ILocationItem>>(
      `${this.locationUrl}/${params.id}`,
      params
    );
  }

  public getLocation(id: string): Promise<ILocationItem> {
    return this.callGet<ILocationItem>(`${this.locationUrl}/${id}`).then(
      response => response.data
    );
  }

  public deleteLocation(id: string): AxiosPromise<void> {
    return this.callDelete(`${this.locationUrl}/${id}`);
  }

  public deleteBulkLocations(ids: string[]): AxiosPromise<void> {
    return this.callDelete(`${this.locationUrl}/ids`, { ids });
  }

  public getOnsiteLocations(
    params?: ITableRequestParams
  ): AxiosPromise<IEnumerableResponse<IOnsiteLocation>> {
    return this.callGet<IEnumerableResponse<IOnsiteLocation>>(
      `${this.locationUrl}/onsite-visitors`,
      params
    );
  }

  public getOnsiteVisitorsInLocation(
    id: string,
    params?: ITableRequestParams
  ): AxiosPromise<IEnumerableResponse<IOnsiteVisitor>> {
    return this.callGet<IEnumerableResponse<IOnsiteVisitor>>(
      `${this.locationUrl}/${id}/onsite-visitors`,
      params
    );
  }

  public getLocationAreas(locationId: string) {
    return this.callGet<IArea[]>(
      `${this.locationUrl}/${locationId}/areas`
    ).then(result => result.data);
  }

  public getArea(locationId: string, areaId: string) {
    return this.callGet<IArea>(
      `${this.locationUrl}/${locationId}/areas/${areaId}`
    ).then(result => result.data);
  }

  public getOnsiteVisitorsInArea(
    locationId: string,
    areaId: string,
    params?: ITableRequestParams
  ) {
    return this.callGet<IEnumerableResponse<IOnsiteVisitor>>(
      `${this.locationUrl}/${locationId}/areas/${areaId}/onsite-visitors`,
      params
    );
  }

  public downloadAreaVisitorsReport({
    locationId,
    areaId,
    params
  }: IDownloadAreaReportParams) {
    return this.callGet<ArrayBuffer>(
      `${this.locationUrl}/${locationId}/areas/${areaId}/onsite-visitors/download`,
      params,
      {
        responseType: 'arraybuffer'
      }
    ).then(result => result.data);
  }

  public downloadLocationVisitorsReport(
    locationId: string,
    params: ITableRequestParams
  ) {
    return this.callGet<ArrayBuffer>(
      `${this.locationUrl}/${locationId}/onsite-visitors/download`,
      params,
      {
        responseType: 'arraybuffer'
      }
    ).then(result => result.data);
  }
}

export default new LocationRepository();
